interface SurveyAnswersValues {
  titleFontFamily?: string;
  titleFontSize?: number;
  titleColor?: string;
  titleFontWeight?: boolean;
  titleFontStyle?: boolean;
  titleTextDecoration?: boolean;
  answerFontFamily?: string;
  answerFontSize?: number;
  answerColor?: string;
  answerFontWeight?: boolean;
  answerFontStyle?: boolean;
  answerTextDecoration?: boolean;
}

export const SURVEY_ANSWERS_CONTENT_SLUG = "survey_answers";

interface UnlayerSurveyAnswersToolHost {
  createViewer?: (viewer: { render: (values: SurveyAnswersValues) => string }) => unknown;
  registerTool: (tool: unknown) => void;
}

interface SurveyAnswersToolDefinition {
  name: string;
  label: string;
  icon: string;
  usageLimit: number;
  supportedDisplayModes: ["email"];
  options: Record<string, unknown>;
  values: SurveyAnswersValues;
}

export function makeSurveyAnswersCustomTool(): string {
  return `(${registerSurveyAnswersCustomTool.toString()})(${JSON.stringify(makeDefinition())});`;
}

function makeDefinition(): SurveyAnswersToolDefinition {
  const FONT_OPTIONS = [
    { value: "Arial, Helvetica, sans-serif", label: "Arial" },
    { value: 'Georgia, "Times New Roman", serif', label: "Georgia" },
    { value: "Verdana, Geneva, sans-serif", label: "Verdana" },
    { value: "Tahoma, Geneva, sans-serif", label: "Tahoma" },
    { value: '"Trebuchet MS", Helvetica, sans-serif', label: "Trebuchet MS" },
    { value: '"Courier New", Courier, monospace', label: "Courier New" },
  ];

  function makeStyleGroup(
    prefix: string,
    title: string,
    position: number,
  ): { title: string; position: number; options: Record<string, unknown> } {
    return {
      title,
      position,
      options: {
        [`${prefix}FontFamily`]: {
          label: "Font Family",
          defaultValue: "Arial, Helvetica, sans-serif",
          widget: "select",
          data: { options: FONT_OPTIONS },
        },
        [`${prefix}FontSize`]: { label: "Font Size", defaultValue: 14, widget: "counter" },
        [`${prefix}Color`]: {
          label: "Color",
          defaultValue: "#000000",
          widget: "color_picker",
        },
        [`${prefix}FontWeight`]: {
          label: "Bold",
          defaultValue: prefix === "title",
          widget: "toggle",
        },
        [`${prefix}FontStyle`]: { label: "Italic", defaultValue: false, widget: "toggle" },
        [`${prefix}TextDecoration`]: { label: "Underline", defaultValue: false, widget: "toggle" },
      },
    };
  }

  return {
    name: SURVEY_ANSWERS_CONTENT_SLUG,
    label: "Survey Answers",
    icon: "fa-list-alt",
    usageLimit: 1,
    supportedDisplayModes: ["email"],
    options: {
      questionStyle: makeStyleGroup("title", "Question Style", 1),
      answerStyle: makeStyleGroup("answer", "Answer Style", 2),
    },
    values: {},
  };
}

function registerSurveyAnswersCustomTool(definition: SurveyAnswersToolDefinition): void {
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  const { unlayer } = globalThis as unknown as { unlayer?: UnlayerSurveyAnswersToolHost };
  if (!unlayer) return;

  // oxlint-disable-next-line unicorn/consistent-function-scoping -- serialised via toString() into the Unlayer iframe; helpers must live inside the function
  function buildStyle(
    values: SurveyAnswersValues,
    prefix: "title" | "answer",
    defaultSize: number,
    margin: string,
  ): string {
    const font =
      values[`${prefix}FontFamily` as keyof SurveyAnswersValues] || "Arial, Helvetica, sans-serif";
    const size = values[`${prefix}FontSize` as keyof SurveyAnswersValues] || defaultSize;
    const color = values[`${prefix}Color` as keyof SurveyAnswersValues] || "#000000";
    const bold = values[`${prefix}FontWeight` as keyof SurveyAnswersValues];
    const italic = values[`${prefix}FontStyle` as keyof SurveyAnswersValues];
    const underline = values[`${prefix}TextDecoration` as keyof SurveyAnswersValues];
    return [
      `margin: ${margin}`,
      `font-family: ${font}`,
      `font-size: ${size}px`,
      `color: ${color}`,
      `font-weight: ${bold ? "bold" : "normal"}`,
      `font-style: ${italic ? "italic" : "normal"}`,
      `text-decoration: ${underline ? "underline" : "none"}`,
    ].join("; ");
  }

  function renderBlock(values: SurveyAnswersValues, question: string, answer: string): string {
    const titleStyle = buildStyle(values, "title", 14, "0 0 8px");
    const answerStyle = buildStyle(values, "answer", 14, "0");
    return (
      `<div id="ce-survey-answers" style="padding: 0 0 24px;">` +
      `<p style="${titleStyle}">${question}</p>` +
      `<p style="${answerStyle}">${answer}</p>` +
      `</div>`
    );
  }

  const renderer: {
    Viewer?: unknown;
    exporters: {
      web: (v: SurveyAnswersValues) => string;
      email: (v: SurveyAnswersValues) => string;
    };
    head: { css: () => string; js: () => string };
  } = {
    exporters: {
      web: (values) => renderBlock(values, "Survey question", "Survey answer"),
      email: (values) => renderBlock(values, "{{this.question}}", "{{this.answer}}"),
    },
    head: { css: () => "", js: () => "" },
  };

  if (typeof unlayer.createViewer === "function") {
    renderer.Viewer = unlayer.createViewer({
      render: (values) => renderBlock(values, "Survey question", "Survey answer"),
    });
  }

  unlayer.registerTool({ ...definition, renderer });
}
